import React from "react";
import { useAuth } from "../../context/AuthContext";
import { useState } from "react";
import { useEffect } from "react";
import {
  getUserDocuments,
  deleteUserDocument,
  createUserDocument,
  getUserDocumentTypes,
} from "../../services/userDocumentService.ts";
import api from "../../lib/api.ts";

const BuyerDocuments = () => {
  const { user } = useAuth();
  const [buyerDocuments, setBuyerDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({});
  const [userDocumentTypes, setUserDocumentTypes] = useState([]);

  useEffect(() => {
    const fetchUserDocumentTypes = async () => {
      const response = await getUserDocumentTypes("buyer", user.userTypeId);

      setUserDocumentTypes(response);
    };
    fetchUserDocumentTypes();

    const fetchBuyerDocuments = async () => {
      try {
        setLoading(true);
        const response = await getUserDocuments("buyer", user.id);
        setBuyerDocuments(response.data);
      } catch (error) {
        console.error("Error fetching documents:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBuyerDocuments();
  }, [user.id, user.userTypeId]);

  // Helper function to get document by slug
  const getDocumentBySlug = (slug) => {
    return buyerDocuments.find(
      (doc) => doc.buyerDocumentType && doc.buyerDocumentType.slug === slug
    );
  };

  // Helper function to get file name from URL
  const getFileName = (url) => {
    if (!url) return "";
    const urlParts = url.split("/");
    const fullFileName = decodeURIComponent(urlParts[urlParts.length - 1]);

    // Truncate filename if it's too long
    if (fullFileName.length > 30) {
      const extension = fullFileName.split(".").pop();
      const nameWithoutExt = fullFileName.substring(
        0,
        fullFileName.lastIndexOf(".")
      );
      const truncatedName = nameWithoutExt.substring(0, 25) + "...";
      return `${truncatedName}.${extension}`;
    }

    return fullFileName;
  };

  // Delete document function
  const handleDeleteDocument = async (documentId) => {
    try {
      await deleteUserDocument("buyer", documentId);
      // Refresh the documents list after deletion
      const response = await getUserDocuments("buyer", user.id);
      setBuyerDocuments(response.data);
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  // Upload document function
  const handleFileUpload = async (file, slug) => {
    if (!file) return;

    try {
      setUploading((prev) => ({ ...prev, [slug]: true }));
      setError(null);

      //  Check if there's an existing document for this slug and delete it
      const existingDocument = getDocumentBySlug(slug);
      if (existingDocument) {
        await deleteUserDocument("buyer", existingDocument.id);
      }

      // Step 1: Upload file to S3
      const formData = new FormData();
      formData.append("images", file);
      formData.append("uploadPath", "userdocuments");

      const uploadResponse = await api.post("/image/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (uploadResponse.data) {
        // Step 2: Create document entry in database
        const documentData = {
          name: file.name,
          url: uploadResponse.data.imageUrls[0],
          slug: slug,
          userDocumentTypeId:
            getDocumentBySlug(slug)?.userDocumentType?.id ||
            getDocumentTypeIdBySlug(slug),
          userId: user.id,
        };

        await createUserDocument("buyer", documentData);

        // Step 3: Refresh documents list
        const response = await getUserDocuments("buyer", user.id);
        setBuyerDocuments(response.data);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      setError("Failed to upload document. Please try again.");
    } finally {
      setUploading((prev) => ({ ...prev, [slug]: false }));
    }
  };

  // Helper function to get document type ID by slug (fallback)
  const getDocumentTypeIdBySlug = (slug) => {
    const staticMapping = {
      "id-front": 1,
      "id-back": 2,
      "trade-license-image": 5,
      "trn-certificate-image": 6,
    };
    return staticMapping[slug];
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='bg-white shadow-md rounded-[8px] sm:rounded-[12px]'>
      <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[8px] sm:rounded-t-[16px] px-1 py-1 border-b border-[#cccccc]'>
        <div className='p-4 sm:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
          <h2 className='text-sm sm:text-base md:text-lg font-semibold'>
            Documents
          </h2>
        </div>
      </div>

      <div className='p-4 sm:p-6'>
        {error && (
          <div className='mb-4 p-3 text-xs sm:text-sm bg-red-50 border border-red-200 text-red-700 rounded'>
            {error}
          </div>
        )}

        {/* First Row - ID Front and ID Back */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6'>
          {userDocumentTypes.slice(0, 2).map((docType) => {
            const document = getDocumentBySlug(docType.slug);
            return (
              <div key={docType.slug} className='space-y-3 sm:space-y-4'>
                <h2 className='text-base sm:text-lg font-semibold text-gray-800 break-words'>
                  {docType.name}
                </h2>

                {/* File Upload Input */}
                <input
                  type='file'
                  accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                  className='w-full px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileUpload(file, docType.slug);
                    }
                  }}
                  disabled={uploading[docType.slug]}
                />

                {uploading[docType.slug] && (
                  <div className='mt-2 text-xs sm:text-sm text-blue-600'>
                    Uploading...
                  </div>
                )}

                {/* Display uploaded file if exists */}
                {document && (
                  <div className='mt-3 p-3 sm:p-3 bg-gray-50 rounded border'>
                    <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                      <div className='flex-1 min-w-0'>
                        <p className='text-xs sm:text-sm font-medium text-gray-800 mb-2 break-words truncate'>
                          {getFileName(document.url)}
                        </p>
                        <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                          Uploaded
                        </span>
                      </div>
                      <div className='flex gap-2 w-full sm:w-auto'>
                        <button
                          onClick={() => window.open(document.url, "_blank")}
                          className='flex-1 sm:flex-none px-3 sm:px-3 py-2 sm:py-1 text-xs sm:text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors font-medium'
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteDocument(document.id)}
                          className='flex-1 sm:flex-none px-3 sm:px-3 py-2 sm:py-1 text-xs sm:text-sm bg-black text-white rounded hover:bg-gray-800 transition-colors font-medium'
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Second Row - TRN Certificate and Trade License (only for corporate users) */}
        {user.userTypeId === 2 && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6'>
            {userDocumentTypes.slice(2, 4).map((docType) => {
              const document = getDocumentBySlug(docType.slug);
              return (
                <div key={docType.slug} className='space-y-3 sm:space-y-4'>
                  <h2 className='text-base sm:text-lg font-semibold text-gray-800 break-words'>
                    {docType.name}
                  </h2>

                  {/* File Upload Input */}
                  <input
                    type='file'
                    accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                    className='w-full px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileUpload(file, docType.slug);
                      }
                    }}
                    disabled={uploading[docType.slug]}
                  />

                  {uploading[docType.slug] && (
                    <div className='mt-2 text-xs sm:text-sm text-blue-600'>
                      Uploading...
                    </div>
                  )}

                  {/* Display uploaded file if exists */}
                  {document && (
                    <div className='mt-3 p-3 sm:p-3 bg-gray-50 rounded border'>
                      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                          <p className='text-xs sm:text-sm font-medium text-gray-800 mb-2 break-words truncate'>
                            {getFileName(document.url)}
                          </p>
                          <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                            Uploaded
                          </span>
                        </div>
                        <div className='flex gap-2 w-full sm:w-auto'>
                          <button
                            onClick={() => window.open(document.url, "_blank")}
                            className='flex-1 sm:flex-none px-3 sm:px-3 py-2 sm:py-1 text-xs sm:text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors font-medium'
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(document.id)}
                            className='flex-1 sm:flex-none px-3 sm:px-3 py-2 sm:py-1 text-xs sm:text-sm bg-black text-white rounded hover:bg-gray-800 transition-colors font-medium'
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerDocuments;
